"use client";
import { useState, useContext, useEffect } from "react";
import { AuthContext } from "../../context/AuthContext";
import axios from "../../config/axios";

type FlashType = { message: string; kind: "success" | "error" } | null;

export default function RegisterPage() {
  const authContext = useContext(AuthContext);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [vatNumber, setVatNumber] = useState("");
  const [flash, setFlash] = useState<FlashType>(null);
  const [submitting, setSubmitting] = useState(false);
  const [registered, setRegistered] = useState(false);

  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 4000);
    return () => clearTimeout(t);
  }, [flash]);

  if (!authContext) return <p>Error loading authentication context</p>;
  const { setUser } = authContext;

  const validateEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  const validatePassword = (p: string) => /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/.test(p);
  const validatePhone = (p: string) => {
    const clean = p.replace(/[^\d+]/g, "");
    const phoneRegex = /^(\+\d{1,3})?[\s\-\(\)]?[\d\s\-\(\)]{10,}$/;
    return phoneRegex.test(p.trim()) && clean.length >= 10;
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setFlash(null);
    if (!name.trim()) return setFlash({ message: "Please enter your name.", kind: "error" });
    if (!validateEmail(email)) return setFlash({ message: "Please enter a valid email.", kind: "error" });
    if (!validatePassword(password)) return setFlash({ message: "Password needs 8+ chars with uppercase, lowercase, number and symbol.", kind: "error" });
    if (!companyName.trim()) return setFlash({ message: "Company name is required.", kind: "error" });
    if (!validatePhone(phone)) return setFlash({ message: "Enter a valid phone number with at least 10 digits. Include country code (e.g. +1).", kind: "error" });

    setSubmitting(true);
    try {
      const { data, status } = await axios.post("/auth/register", {
        name,
        email,
        password,
        phone,
        company_name: companyName,
        vat_number: vatNumber,
      });
      if (status === 200) {
        setUser(data.user);
        setRegistered(true);
      } else {
        setFlash({ message: "Registration error.", kind: "error" });
      }
    } catch {
      setFlash({ message: "Registration error. Please try again.", kind: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  if (registered) {
    return (
      <div className="bg-gray-50 min-h-[70vh] flex items-start justify-center py-12 px-4">
        <div className="w-full max-w-md bg-white shadow-sm border border-gray-200 rounded-lg p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-green-100 text-green-600 mx-auto flex items-center justify-center mb-4">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Account created</h2>
          <p className="text-sm text-gray-600 mb-5">You&apos;re signed in and ready to browse the wholesale catalog.</p>
          <button
            onClick={() => (window.location.href = "/shop")}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2.5 rounded-lg transition"
          >
            Go to catalog
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-[70vh] flex items-start justify-center py-12 px-4">
      <div className="w-full max-w-xl">
        <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-8">
          <h1 className="text-2xl font-bold text-gray-800 mb-1">Apply for wholesale</h1>
          <p className="text-sm text-gray-500 mb-6">Create your B2B account to access the catalog and pricing.</p>

          {flash && (
            <div className={`mb-4 p-3 rounded text-sm border ${flash.kind === "success" ? "bg-green-50 border-green-200 text-green-800" : "bg-red-50 border-red-200 text-red-700"}`}>
              {flash.message}
            </div>
          )}

          <form onSubmit={handleRegister} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className="block text-sm text-gray-700 mb-1">Full name <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-300 transition"
                placeholder="John Doe"
                autoComplete="name"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm text-gray-700 mb-1">Company name <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-300 transition"
                placeholder="Example Company Ltd."
                autoComplete="organization"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-300 transition"
                placeholder="you@company.com"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">Phone <span className="text-red-500">*</span></label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-300 transition"
                placeholder="+1 (416) 555-1234"
                autoComplete="tel"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm text-gray-700 mb-1">Password <span className="text-red-500">*</span></label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-300 transition"
                placeholder="8+ chars with uppercase, number, symbol"
                autoComplete="new-password"
              />
              <p className="text-xs text-gray-500 mt-1">Must include uppercase, lowercase, number and special character.</p>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm text-gray-700 mb-1">VAT number <span className="text-gray-400 font-normal">(optional)</span></label>
              <input
                type="text"
                value={vatNumber}
                onChange={(e) => setVatNumber(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-300 transition"
                placeholder="Tax ID if applicable"
              />
            </div>
            <div className="md:col-span-2 mt-2">
              <button
                type="submit"
                disabled={submitting}
                className={`w-full py-2.5 rounded-lg font-semibold text-white transition ${
                  submitting ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {submitting ? "Creating account…" : "Create account"}
              </button>
            </div>
          </form>

          <p className="mt-5 text-sm text-gray-600 text-center">
            Already have an account?{" "}
            <a href="/login" className="text-blue-600 hover:text-blue-700 font-medium">
              Sign in
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
